import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { UserCog, Truck } from 'lucide-react';

// Assign a Work Order to EITHER an internal field user OR an external service provider.
export const AssignDialog = ({ wo, fieldUsers, providers, onClose, onDone }) => {
  const { token } = useContext(AuthContext);
  const [mode, setMode] = useState(wo.service_provider_id ? 'provider' : 'internal');
  const [userId, setUserId] = useState(wo.assigned_to || '');
  const [providerId, setProviderId] = useState(wo.service_provider_id || '');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Fastest response (hours) from a provider's commitments; null = unknown.
  const respHours = (p) => {
    const hrs = (p.priority_commitments || []).map((c) => parseFloat(c.response_hours)).filter((n) => !Number.isNaN(n));
    return hrs.length ? Math.min(...hrs) : null;
  };
  const orderedProviders = React.useMemo(() => (providers || []).map((p) => {
    const h = respHours(p);
    const label = h != null ? `~${h}h` : (p.accepts_p1 === true ? 'P1 accepted (confirm ETA)' : 'response unknown');
    return { ...p, _respHours: h, _respLabel: label };
  }).sort((a, b) => {
    if (a._respHours == null && b._respHours == null) return 0;
    if (a._respHours == null) return 1;
    if (b._respHours == null) return -1;
    return a._respHours - b._respHours;
  }), [providers]);
  const selectedProvider = orderedProviders.find((p) => p.id === providerId);

  const submit = async (unassign = false) => {
    setBusy(true);
    let body;
    if (unassign) {
      body = { fulfillment_type: 'Unassigned', assigned_to: null, service_provider_id: null, notes };
    } else if (mode === 'internal') {
      if (!userId) { toast.error('Select an internal field user'); setBusy(false); return; }
      body = { fulfillment_type: 'TFS Internal Personnel', assigned_to: userId, service_provider_id: null, notes };
    } else {
      if (!providerId) { toast.error('Select a service provider'); setBusy(false); return; }
      body = { fulfillment_type: 'Third-Party Service Provider', assigned_to: null, service_provider_id: providerId, notes };
    }
    try {
      await axios.post(`${API}/work-orders/${wo.id}/assignment`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(unassign ? 'Work order unassigned' : 'Work order assigned');
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not assign');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47] flex items-center gap-2"><UserCog size={18} className="text-[#8FC93A]" /> Assign {wo.tfs_work_order_number}</DialogTitle>
          <DialogDescription>Assign to internal field personnel or an external service provider.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setMode('internal')} data-testid="assign-mode-internal"
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${mode === 'internal' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200'}`}>Internal Field</button>
            <button onClick={() => setMode('provider')} data-testid="assign-mode-provider"
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${mode === 'provider' ? 'bg-[#0E2A47] text-white border-[#0E2A47]' : 'bg-white text-slate-600 border-gray-200'}`}>Service Provider</button>
          </div>
          {mode === 'internal' ? (
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Field User</Label>
              <select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="assign-user-select"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">Select…</option>
                {fieldUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
          ) : (
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Service Provider (Qualified only — ordered by fastest response)</Label>
              <select value={providerId} onChange={(e) => setProviderId(e.target.value)} data-testid="assign-provider-select"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">Select…</option>
                {orderedProviders.map((p) => <option key={p.id} value={p.id}>{p.company_name}{p._respLabel ? ` — ${p._respLabel}` : ''}</option>)}
              </select>
              {providers.length === 0 && <p className="text-xs text-slate-400">No qualified active providers — qualify one in the Service Providers directory.</p>}
              {selectedProvider && (
                <div className="rounded-md bg-slate-50 p-2 text-xs text-slate-600" data-testid="assign-provider-info">
                  <div>Response: {selectedProvider._respLabel || 'Unknown — confirm ETA with provider'}</div>
                  {selectedProvider.accepts_p1 === false && <div className="mt-0.5 font-semibold text-amber-600">⚠ Does not accept P1 emergencies — confirm an agreed scheduling exception.</div>}
                  {selectedProvider._respHours == null && <div className="mt-0.5 text-amber-600">⚠ Response time not on file — record a confirmed ETA before dispatch.</div>}
                </div>
              )}
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {(wo.assigned_to || wo.service_provider_id) && (
            <button onClick={() => submit(true)} disabled={busy} data-testid="assign-unassign"
              className="mr-auto rounded-md border border-gray-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Unassign</button>
          )}
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={() => submit(false)} disabled={busy} data-testid="assign-confirm"
            className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : 'Assign'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Schedule a Work Order (date + service window).
export const ScheduleDialog = ({ wo, onClose, onDone }) => {
  const { token } = useContext(AuthContext);
  const [date, setDate] = useState((wo.scheduled_date || '').slice(0, 10));
  const [window, setWindow] = useState(wo.service_window || '');
  const [notes, setNotes] = useState(wo.scheduling_notes || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!date) { toast.error('Pick a scheduled date'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${wo.id}/schedule`,
        { scheduled_date: date, service_window: window || null, scheduling_notes: notes || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Work order scheduled');
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not schedule');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47]">Schedule {wo.tfs_work_order_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Scheduled Date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="schedule-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Service Window</Label>
            <Input value={window} onChange={(e) => setWindow(e.target.value)} placeholder="e.g. 8:00 AM – 12:00 PM" data-testid="schedule-window" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Scheduling Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="schedule-confirm"
            className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : 'Schedule'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
